import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { EMPTY, Observable, catchError, finalize, of, take } from 'rxjs';
import { AuthService, ConfigService, FramesService } from '@app/core/services';
import { ConfigEnum } from '@app/enums';
import { LoadingService } from '@app/shared/services/loading.service';

@Component({
  selector: 'login-dialog',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressBarModule, MatSelectModule, MatDialogModule],
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss'],
})
export class LoginDialogComponent implements OnInit {
  constructor(
    private readonly authService: AuthService,
    private readonly frameService: FramesService,
    private loadingService: LoadingService,
    private configService: ConfigService,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  isAuthenticated$ = this.authService.isAuthenticated$;
  framesUser$ = this.authService.framesUser$;

  errorMessage = '';
  users: string[] | undefined;
  visibility$ = this.loadingService.loading$;
  isReady$ = this.frameService.ready$;

  ngOnInit(): void {
    // Wait for access token, then fetch users (only for admin accounts)
    this.authService.accessToken$.pipe(take(1)).subscribe({
      next: () => {
        if (this.data.isAdmin) {
          this.fetchUsers();
        }
      },
      error: (error) => {
        this.errorMessage = this.authService.errorDescription || 'Authentication failed';
        this.loadingService.show();
        setTimeout(() => {
          this.loadingService.hide();
          this.authService.logout();
        }, 3000);
      },
    });
  }

  private fetchUsers(): void {
    this.getUsers$().subscribe({
      next: (users) => {
        this.users = users;
      },
      error: (error) => {
        this.errorMessage = error.error || 'Failed to fetch users';
        this.loadingService.show();
        setTimeout(() => {
          this.loadingService.hide();
          this.authService.logout();
        }, 3000);
      },
    });
  }

  userOnChange(event: any) {
    this.data.onUserSelectedEvent(event.target.value);
  }

  onLogout() {
    this.authService.logout();
  }

  getUsers$(): Observable<string[]> {
    this.loadingService.show();
    if (this.configService.get(ConfigEnum.Production)) {
      return of(this.configService.get(ConfigEnum.Auth0Users)); // Return users from config in production
    } else {
      return this.http.get<string[]>('oauth/users').pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = error.error;
          return EMPTY; // Return an empty observable on error
        }),
        finalize(() => {
          this.loadingService.hide();
        }),
      );
    }
  }
}
